import React, { useState, useEffect } from "react";
import { FaTruck } from "react-icons/fa";
import { Header, DateRangePicker, RouteCard } from "../../../components/ui";
import PageLayout from "../../../components/layout/PageLayout";
import api from "../../../services/api";
import RouteModal from "./modal/RouteModal";
import LogistSummaryPanel from "../../../components/ui/LogistSummaryPanel";

export default function LogistRoutes() {
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedRoute, setSelectedRoute] = useState(null);

  // Формат времени
  const formatDuration = (startTime) => {
    if (!startTime) return null;
    const start = new Date(startTime);
    if (Number.isNaN(start.getTime())) return null;

    const diffMs = Date.now() - start.getTime();
    if (diffMs < 0) return null;

    const minutes = Math.floor(diffMs / 60000);
    const hours = Math.floor(minutes / 60);
    const mins = minutes % 60;

    if (hours === 0) return `${mins} мин`;
    if (hours < 24) return `${hours} ч ${mins} мин`;
    const days = Math.floor(hours / 24);
    return `${days} дн ${hours % 24} ч`;
  };

  // Определение движения маршрута
  const computeMovement = (receipts = []) => {
    const events = [];
    for (const r of receipts) {
      if (r.arrived_at) {
        const d = new Date(r.arrived_at);
        if (!isNaN(d.getTime())) {
          events.push({ type: "arrived_at", ts: d, address: r.delivery_address || "-" });
        }
      }
      if (r.departed_at) {
        const d = new Date(r.departed_at);
        if (!isNaN(d.getTime())) {
          events.push({ type: "departed_at", ts: d, address: r.delivery_address || "-" });
        }
      }
    }

    if (events.length === 0) {
      return { currentStatus: "—", fromAddress: "-", toAddress: "-", timeSpent: null };
    }

    events.sort((a, b) => a.ts - b.ts);
    const last = events[events.length - 1];

    if (last.type === "arrived_at") {
      return {
        currentStatus: "Стоит на точке",
        fromAddress: last.address,
        toAddress: "-",
        timeSpent: formatDuration(last.ts),
      };
    }

    let prevArrived = null;
    for (let i = events.length - 2; i >= 0; i--) {
      const e = events[i];
      if (e.type === "arrived_at" && e.ts < last.ts) {
        prevArrived = e;
        break;
      }
    }

    return {
      currentStatus: "В пути",
      fromAddress: prevArrived?.address || "-",
      toAddress: last.address || "-",
      timeSpent: formatDuration(last.ts),
    };
  };

  // Загрузка маршрутов
  const fetchRoutes = async (from, to) => {
    try {
      setLoading(true);
      const res = await api.get("/routes/by-date", {
        params: { startDate: from, endDate: to },
      });

      const routesData = (res || []).map((route) => {
        const receipts = route.receipts || [];
        const movement = computeMovement(receipts);
        return { ...route, ...movement };
      });

      setRoutes(routesData);
    } catch (err) {
      console.error("Ошибка загрузки маршрутов:", err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    const today = new Date().toISOString().split("T")[0];
    fetchRoutes(today, today);
  }, []);

  const handleDateChange = ({ from, to }) => fetchRoutes(from, to);

  // === Сводка ===
  const totalRoutes = routes.length;
  const totalReceipts = routes.reduce((a, r) => a + (r.receipts?.length || 0), 0);
  const totalDelivered = routes.reduce(
    (a, r) => a + (r.receipts?.filter((x) => x.status === "Закрыта").length || 0),
    0
  );
  const totalNotDelivered = routes.reduce(
    (a, r) => a + (r.receipts?.filter((x) => x.status === "Не доставлено").length || 0),
    0
  );
  const totalOpen = routes.reduce(
    (a, r) => a + (r.receipts?.filter((x) => x.status === "Открыта").length || 0),
    0
  );

  const totalAmount = routes.reduce(
    (sum, r) =>
      sum +
      (r.receipts || []).reduce(
        (acc, x) => acc + (parseFloat(x.amount) || 0),
        0
      ),
    0
  );

  const avgDeliveryTime = "3 ч 25 мин";

  const summaryData = {
    routesTotal: totalRoutes,
    receiptsTotal: totalReceipts,
    delivered: totalDelivered,
    delayed: totalNotDelivered,
    open: totalOpen,
    avgDeliveryTime,
    totalAmount: `${totalAmount.toLocaleString("ru-RU")} ₽`,
    activeDrivers: new Set(routes.map((r) => r.driver).filter(Boolean)).size,
    topDriver:
      routes
        .filter((r) => r.driver)
        .sort(
          (a, b) =>
            (b.receipts?.filter((x) => x.status === "Закрыта").length || 0) -
            (a.receipts?.filter((x) => x.status === "Закрыта").length || 0)
        )[0]?.driver || "—",
  };

  return (
    <PageLayout
      header={
        <Header
          icon={FaTruck}
          title="Маршруты"
          subtitle="Просмотр и анализ маршрутов за выбранный период"
        />
      }
      controls={<DateRangePicker mode="range" onChange={handleDateChange} />}
      stats={<LogistSummaryPanel data={summaryData} />}
    >
      {loading ? (
        <p style={styles.loading}>Загрузка маршрутов...</p>
      ) : routes.length === 0 ? (
        <p style={styles.empty}>Маршрутов за выбранный период не найдено.</p>
      ) : (
        <div style={styles.cards}>
          {routes.map((route) => (
            <div
              key={route.id}
              style={styles.cardWrapper}
              onClick={() => setSelectedRoute(route)}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-3px)";
                e.currentTarget.style.boxShadow = "0 4px 10px rgba(0,0,0,0.1)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "translateY(0)";
                e.currentTarget.style.boxShadow = "none";
              }}
            >
              <RouteCard route={route} />
            </div>
          ))}
        </div>
      )}

      {selectedRoute && (
        <RouteModal route={selectedRoute} onClose={() => setSelectedRoute(null)} />
      )}
    </PageLayout>
  );
}

const styles = {
  loading: {
    color: "#555",
  },
  empty: {
    color: "#999",
  },
  cards: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fill, minmax(300px, 1fr))",
    gap: "1rem",
    marginTop: "1rem",
  },
  cardWrapper: {
    cursor: "pointer",
    transition: "transform 0.2s ease, box-shadow 0.2s ease",
  },
};
